import React from 'react'
import { Link } from '../../../components/common/link/Link'
import { css } from '@emotion/react'
import { t, Trans } from '@lingui/macro'
import { Box, ButtonPrimary, Flex, Input } from '../../../components'
import { useLoginContext } from '../login/LoginContext'
import Label from '../../../components/common/form/label/Label'
import { useInputMaskingToggle } from '../useInputMaskingToggle'
import emailRegex from '../../../app/services/emailRegex'

import IconArrowRight from '../../../components/common/icon/IconArrowRight'
import { usePublicApiSession } from '../../../public-api/hooks/usePublicApiSession'
import { ForgotPasswordLink } from '../login/components/ForgotPasswordLink'
import Form from '../../../components/common/form/Form'
import { WelcomeMessage } from '../login/components/WelcomeMessage'
import { OauthLoginButtons } from '../components/OauthLoginButtons'
import { SignToDifferentTeamLink } from '../login/components/SignToDifferentTeamLink'
import Loader from '../../../components/common/loader/Loader'
import { observer } from 'mobx-react'
import { useAsyncCallback } from '../useAsyncCallback'

type LoginStepAskPasswordProps = {
  submit(): Promise<boolean>
  backToEmailStep(): void
}

export const LoginStepAskPassword: React.FC<LoginStepAskPasswordProps> =
  observer(({ submit, backToEmailStep }) => {
    const { userIdentity, isAccountNameLoginOn, setPassword } =
      useLoginContext()
    const { isUnmasked, eyeButton } = useInputMaskingToggle()
    const isEmail = userIdentity && !!emailRegex.exec(userIdentity)
    const { refetch } = usePublicApiSession()

    const [handleSubmit, loading] = useAsyncCallback(async () => {
      if (await submit()) {
        await refetch()
      }
    })

    if (loading) {
      return <Loader />
    }
    return (
      <Form onSubmit={handleSubmit}>
        <WelcomeMessage />

        <Flex className="changeEmail">
          <span>
            {isEmail ? t`Email` : t`Account name`}{' '}
            <strong>{userIdentity}</strong>{' '}
          </span>
          <Link
            onClick={backToEmailStep}
            css={css`
              margin-left: 8px;
            `}
          >
            <Trans>change</Trans>
          </Link>
        </Flex>

        <Flex justifyContent="space-between">
          <Label htmlFor="inputPassword">
            <Trans>Enter your password</Trans>
          </Label>
          {eyeButton}
        </Flex>
        <Box className="form-row">
          <input
            type={isAccountNameLoginOn ? 'text' : 'email'}
            id="inputEmail"
            autoComplete={isAccountNameLoginOn ? 'username' : 'email'}
            style={{
              display: 'none' // this input is here just to get rid of chrome warning and improve accessibility https://stackoverflow.com/a/48736294/671457
            }}
          />
          <Input
            type={isUnmasked ? 'text' : 'password'}
            id="inputPassword"
            placeholder={t`Password`}
            autoComplete="current-password"
            autoFocus
            required
            onChange={(e) => setPassword(e.target.value)}
          />
        </Box>

        <Box className="form-row">
          <ButtonPrimary
            type="submit"
            disabled={loading}
            isLoading={loading}
            isFullWidth
            mt="20px"
            rightIcon={<IconArrowRight />}
          >
            <span className="btn__text">
              <Trans>Log in</Trans>
            </span>
          </ButtonPrimary>
        </Box>

        <ForgotPasswordLink />
        <OauthLoginButtons />
        <SignToDifferentTeamLink />
      </Form>
    )
  })

LoginStepAskPassword.displayName = 'LoginStepAskPassword'
